import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { CATEGORY_ORDER, CATEGORY_EMOJI } from '../utils/ingredientUtils';
import '../styles/AddRecipe.css';
import { useData } from '../contexts/DataContext';

const CATEGORIES = CATEGORY_ORDER;
const BLANK = { name: '', category: 'Produce', aliases: '' };

// ─────────────────────────────────────────────────────────────────────────────
// ALIAS TAG INPUT
// ─────────────────────────────────────────────────────────────────────────────
function AliasInput({ value, onChange }) {
  const [draft, setDraft] = useState('');
  const aliases = value ? value.split(',').map(a => a.trim()).filter(Boolean) : [];

  const addAlias = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (!aliases.includes(trimmed)) onChange([...aliases, trimmed].join(', '));
    setDraft('');
  };

  const removeAlias = (alias) => onChange(aliases.filter(a => a !== alias).join(', '));

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
        <input
          type="text" value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }}
          placeholder="Type alias and press Enter or Add…"
          style={{ flex: 1 }}
        />
        <button type="button" className="add-button" onClick={addAlias} style={{ whiteSpace: 'nowrap' }}>Add</button>
      </div>
      <div className="tag-list">
        {aliases.map((a, i) => (
          <span key={i} className="tag">{a}<button className="tag-remove" type="button" onClick={() => removeAlias(a)}>×</button></span>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INLINE EDIT FORM — rendered below the ingredient row being edited
// ─────────────────────────────────────────────────────────────────────────────
function InlineEditForm({ ingredient, onSave, onCancel, saveStatus }) {
  const [fields, setFields] = useState({
    name:     ingredient.name,
    category: ingredient.category,
    aliases:  ingredient.aliases || '',
  });
  const set = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

  return (
    <div style={{
      background: '#fffde7', border: '1px solid #FFD54F',
      borderRadius: '6px', padding: '12px 14px', margin: '4px 8px 8px',
    }}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div className="form-field" style={{ flex: '1 1 180px' }}>
          <label className="form-label">Name</label>
          <input
            type="text" value={fields.name}
            disabled
            style={{ background: '#f5f5f5', color: '#888' }}
          />
        </div>
        <div className="form-field" style={{ flex: '1 1 160px' }}>
          <label className="form-label">Category</label>
          <select value={fields.category} onChange={e => set('category', e.target.value)}>
            {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
      </div>
      <div className="form-field" style={{ marginBottom: '10px' }}>
        <label className="form-label">Aliases <span style={{ fontWeight: 400, color: '#888' }}>(alternate names for search)</span></label>
        <AliasInput value={fields.aliases} onChange={(val) => set('aliases', val)} />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          className="add-button"
          onClick={() => onSave(fields)}
          disabled={saveStatus === 'saving'}
          style={{ opacity: saveStatus === 'saving' ? 0.6 : 1 }}
        >
          {saveStatus === 'saving' ? '⏳ Saving…' : saveStatus === 'saved' ? '✓ Saved!' : 'Update'}
        </button>
        <button className="edit-button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function AddIngredients() {
  const { ingredients: rawIngredients, saveIngredient, deleteIngredient } = useData();

  const ingredients = rawIngredients.map(i => ({
    ...i,
    aliases: Array.isArray(i.aliases) ? i.aliases.join(', ') : (i.aliases || ''),
  }));

  const [fields,        setFields]        = useState(BLANK);
  const [editingName,   setEditingName]    = useState(null); // name of ingredient being edited
  const [search,        setSearch]         = useState('');
  const [filterCat,     setFilterCat]      = useState('');
  const [showJSON,      setShowJSON]       = useState(false);
  const [copied,        setCopied]         = useState(false);
  const [deleteConfirm, setDeleteConfirm]  = useState(null);
  const [addSaveStatus, setAddSaveStatus]  = useState('');
  const [editSaveStatus,setEditSaveStatus] = useState('');
  const [savedName,     setSavedName]      = useState('');

  const set = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

  const [collapsedCategories, setCollapsedCategories] = useState(
    () => Object.fromEntries(CATEGORY_ORDER.map(cat => [cat, true]))
  );
  const toggleCategory = (cat) => setCollapsedCategories(prev => ({ ...prev, [cat]: prev[cat] === false ? true : false }));

  // ── Filtered / grouped view ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return ingredients.filter(i => {
      const matchCat  = !filterCat || i.category === filterCat;
      const matchName = !q || i.name.toLowerCase().includes(q) ||
        (i.aliases || '').toLowerCase().includes(q);
      return matchCat && matchName;
    });
  }, [ingredients, search, filterCat]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(i => {
      if (!map[i.category]) map[i.category] = [];
      map[i.category].push(i);
    });
    return CATEGORY_ORDER
      .filter(cat => map[cat]?.length)
      .map(cat => ({ category: cat, items: map[cat].sort((a, b) => a.name.localeCompare(b.name)) }));
  }, [filtered]);

  // ── Add new ingredient ─────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!fields.name.trim()) return;
    setAddSaveStatus('saving');
    try {
      await saveIngredient({
        name:     fields.name.trim(),
        category: fields.category,
        aliases:  fields.aliases ? fields.aliases.split(',').map(a => a.trim()).filter(Boolean) : [],
      });
      const savedIngredientName = fields.name.trim();
      setFields(BLANK);
      setAddSaveStatus('saved');
      setSavedName(savedIngredientName);
      setTimeout(() => { setAddSaveStatus(''); setSavedName(''); }, 3000);
    } catch (err) {
      console.error(err);
      setAddSaveStatus('error');
    }
  };

  // ── Inline edit save ───────────────────────────────────────────────────────
  const handleEditSave = async (updatedFields) => {
    setEditSaveStatus('saving');
    try {
      await saveIngredient({
        name:     updatedFields.name.trim(),
        category: updatedFields.category,
        aliases:  updatedFields.aliases
          ? updatedFields.aliases.split(',').map(a => a.trim()).filter(Boolean)
          : [],
      });
      setEditingName(null);
      setEditSaveStatus('saved');
      setTimeout(() => setEditSaveStatus(''), 2000);
    } catch (err) {
      console.error(err);
      setEditSaveStatus('error');
    }
  };

  const handleDelete = async (name) => {
    try {
      await deleteIngredient(name);
      setDeleteConfirm(null);
      if (editingName === name) setEditingName(null);
    } catch (err) {
      console.error(err);
      alert('Error deleting ingredient.');
    }
  };

  // ── JSON export ────────────────────────────────────────────────────────────
  const exportJSON = () => {
    const data = ingredients.map(i => ({
      name:    i.name,
      category: i.category,
      aliases:  i.aliases ? i.aliases.split(',').map(a => a.trim()).filter(Boolean) : [],
    }));
    return JSON.stringify(data, null, 2);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
      <div className="add-recipe-container">
        <h1 className="page-title">🧅 Manage Ingredients</h1>
        <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.92rem' }}>
          Add or edit ingredients used for recipe search autocomplete. Changes are saved directly to Firebase.
        </p>

        {/* ── Add New Ingredient form (always visible at top) ── */}
        <section className="form-section">
          <h2 className="section-title">Add New Ingredient</h2>

          <div className="form-grid-2" style={{ marginBottom: '12px' }}>
            <div className="form-field">
              <label className="form-label">Name *</label>
              <input
                type="text" value={fields.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. chicken breast"
                style={{ borderColor: ingredients.find(i => i.name.toLowerCase() === fields.name.toLowerCase().trim()) ? '#ff9800' : undefined }}
              />
              {(() => {
                const existing = fields.name.trim()
                  ? ingredients.find(i => i.name.toLowerCase() === fields.name.toLowerCase().trim())
                  : null;
                return existing ? (
                  <div style={{ marginTop: '5px', padding: '6px 10px', background: '#FFF3E0', border: '1px solid #ff9800', borderRadius: '5px', fontSize: '0.78rem', color: '#E65100', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>⚠️</span>
                    <span><strong>"{existing.name}"</strong> already exists in <strong>{existing.category}</strong>{existing.aliases ? ` — aliases: ${existing.aliases}` : ''}. Use Edit to update it.</span>
                  </div>
                ) : null;
              })()}
            </div>
            <div className="form-field">
              <label className="form-label">Category</label>
              <select value={fields.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
          </div>

          <div className="form-field" style={{ marginBottom: '12px' }}>
            <label className="form-label">Aliases <span style={{ fontWeight: 400, color: '#888' }}>(alternate names for search)</span></label>
            <AliasInput value={fields.aliases} onChange={(val) => set('aliases', val)} />
          </div>

          <button
            className="add-button"
            onClick={handleAdd}
            disabled={!fields.name.trim() || addSaveStatus === 'saving' || !!ingredients.find(i => i.name.toLowerCase() === fields.name.toLowerCase().trim())}
            style={{ opacity: (!fields.name.trim() || !!ingredients.find(i => i.name.toLowerCase() === fields.name.toLowerCase().trim())) ? 0.5 : 1 }}
          >
            {addSaveStatus === 'saving' ? '⏳ Saving…' : 'Add Ingredient'}
          </button>

          {addSaveStatus === 'saved' && savedName && (
            <div style={{
              marginTop: '10px', padding: '8px 14px',
              background: '#F0FBF0', border: '1px solid #4CAF50',
              borderRadius: '6px', fontSize: '0.88rem', color: '#2E7D32',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span>✅</span>
              <span><strong>"{savedName}"</strong> has been added to your ingredient list.</span>
            </div>
          )}
        </section>

        {/* ── Filters + ingredient list ── */}
        <section className="form-section">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
            <input
              type="text" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search ingredients…"
              style={{ flex: '1 1 200px' }}
            />
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ flex: '1 1 160px' }}>
              <option value="">All Categories</option>
              {CATEGORY_ORDER.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <button
              className="edit-button"
              style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '4px 10px' }}
              onClick={() => {
                const allCollapsed = grouped.every(({ category }) => collapsedCategories[category] !== false);
                setCollapsedCategories(prev => {
                  const updated = { ...prev };
                  grouped.forEach(({ category }) => { updated[category] = allCollapsed ? false : true; });
                  return updated;
                });
              }}
            >
              {grouped.every(({ category }) => collapsedCategories[category] !== false) ? '▾ Expand All' : '▸ Collapse All'}
            </button>
            <span style={{ color: '#888', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
              {filtered.length} ingredient{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          {grouped.map(({ category, items }) => {
            const emoji = CATEGORY_EMOJI[category] || '🥄';
            return (
              <div key={category} style={{ marginBottom: '12px', border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                {/* Category header */}
                <div
                  onClick={() => toggleCategory(category)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: '#fafafa', cursor: 'pointer', userSelect: 'none' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fafafa'}
                >
                  <span style={{ fontWeight: 700, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{emoji}</span>{category}
                    <span style={{ fontSize: '0.72rem', color: '#aaa', fontWeight: 400 }}>({items.length})</span>
                  </span>
                  <span style={{ color: '#aaa', fontSize: '0.75rem' }}>{collapsedCategories[category] !== false ? '▸' : '▾'}</span>
                </div>

                {/* Ingredient rows */}
                {collapsedCategories[category] === false && (
                  <div style={{ background: 'white' }}>
                    {items.map((ing) => (
                      <div key={ing.name}>
                        {/* Row */}
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '7px 14px', borderTop: '1px solid #f5f5f5', fontSize: '0.88rem',
                          background: editingName === ing.name ? '#fffde7' : 'white',
                        }}>
                          <div>
                            <span style={{ fontWeight: 600 }}>{ing.name}</span>
                            {ing.aliases && (
                              <span style={{ marginLeft: '8px', color: '#aaa', fontSize: '0.78rem' }}>
                                {ing.aliases}
                              </span>
                            )}
                            {editingName === ing.name && (
                              <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#E67E22', fontWeight: 600 }}>
                                ✏️ editing…
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <button
                              className="edit-button"
                              style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                              onClick={() => setEditingName(editingName === ing.name ? null : ing.name)}
                            >
                              {editingName === ing.name ? 'Close' : 'Edit'}
                            </button>
                            {deleteConfirm === ing.name ? (
                              <>
                                <button
                                  onClick={() => handleDelete(ing.name)}
                                  style={{ fontSize: '0.72rem', padding: '2px 8px', background: '#ff4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                >Confirm</button>
                                <button
                                  className="edit-button"
                                  style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                                  onClick={() => setDeleteConfirm(null)}
                                >Cancel</button>
                              </>
                            ) : (
                              <button
                                className="remove-button"
                                style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                                onClick={() => setDeleteConfirm(ing.name)}
                              >Delete</button>
                            )}
                          </div>
                        </div>

                        {/* Inline edit form — shown directly below this row */}
                        {editingName === ing.name && (
                          <InlineEditForm
                            ingredient={ing}
                            onSave={handleEditSave}
                            onCancel={() => setEditingName(null)}
                            saveStatus={editSaveStatus}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* ── JSON export ── */}
        <section className="form-section">
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button className="edit-button" onClick={() => setShowJSON(v => !v)}>
              {showJSON ? 'Hide' : 'Export JSON'}
            </button>
            {showJSON && (
              <button className="edit-button" onClick={() => {
                navigator.clipboard.writeText(exportJSON());
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}>
                {copied ? '✓ Copied!' : 'Copy'}
              </button>
            )}
          </div>
          {showJSON && (
            <textarea
              readOnly value={exportJSON()}
              style={{ width: '100%', boxSizing: 'border-box', height: '300px', fontFamily: 'monospace', fontSize: '0.78rem', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', background: '#fafafa', marginTop: '10px', resize: 'vertical' }}
            />
          )}
        </section>
      </div>
    </motion.div>
  );
}

export default AddIngredients;
